import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { OutcomeStatus } from "@/lib/types";

/** Small uppercase mono label — the system voice of the terminal. */
export function MonoLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("mono-label", className)}>{children}</span>;
}

/** Pulsing status dot + label. */
export function StatusDot({
  tone = "cyan",
  label,
  className,
  testId,
}: {
  tone?: "cyan" | "success" | "warn" | "error";
  label: string;
  className?: string;
  testId?: string;
}) {
  const color =
    tone === "success" ? "bg-success" : tone === "warn" ? "bg-warn" : tone === "error" ? "bg-error" : "bg-cyan";
  return (
    <span
      className={cn(
        "relative z-10 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-dim",
        className
      )}
      data-testid={testId}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className={cn("absolute inline-flex h-full w-full animate-pulse-dot rounded-full", color)} />
        <span className={cn("relative inline-flex h-1.5 w-1.5 rounded-full", color)} />
      </span>
      {label}
    </span>
  );
}

const STATUS_TONE: Record<OutcomeStatus, { border: string; text: string; bg: string; dot: string }> = {
  VERIFIED: { border: "border-success/40", text: "text-success", bg: "bg-success/10", dot: "bg-success" },
  ACTIVE: { border: "border-cyan/40", text: "text-cyan", bg: "bg-cyan/10", dot: "bg-cyan" },
  FAILED: { border: "border-error/40", text: "text-error", bg: "bg-error/10", dot: "bg-error" },
  DISPUTED: { border: "border-warn/40", text: "text-warn", bg: "bg-warn/10", dot: "bg-warn" },
};

export function StatusBadge({ status, className }: { status: OutcomeStatus; className?: string }) {
  const t = STATUS_TONE[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]",
        t.border,
        t.text,
        t.bg
      )}
    >
      <span className={cn("h-1 w-1 rounded-full", t.dot)} />
      {status}
    </span>
  );
}

/** Data panel with HUD header: title, protocol id/meta, corner ticks. */
export function DataPanel({
  title,
  meta,
  children,
  className,
  bodyClassName,
  scan = true,
}: {
  title: string;
  meta?: string;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  scan?: boolean;
}) {
  return (
    <div className={cn("panel hud-corners", scan && "scanlines", className)}>
      <div className="relative z-10 flex items-center justify-between border-b border-line px-4 py-2.5">
        <MonoLabel className="text-[10px] text-ink/80">{title}</MonoLabel>
        {meta && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-dim/80">{meta}</span>}
      </div>
      <div className={cn("relative z-10 p-5", bodyClassName)}>{children}</div>
    </div>
  );
}

/** Buttons — terminal aesthetic, sharp with a notched corner on primary. */
export const btnPrimary =
  "clip-notch inline-flex items-center justify-center gap-2 whitespace-nowrap bg-blue px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-electric hover:text-base disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-blue disabled:hover:text-white";
export const btnOutline =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap border border-electric/40 bg-transparent px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan transition-colors duration-200 hover:border-cyan hover:bg-cyan/10";
export const btnGhost =
  "inline-flex items-center justify-center gap-2 px-3 py-2 font-mono text-xs font-medium uppercase tracking-[0.2em] text-dim transition-colors duration-200 hover:text-cyan";

/** Official X logo mark. */
export function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={cn("h-4 w-4", className)}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/** Live UTC clock — one interval, shared placement. */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}
